import { jsPDF } from "jspdf";
import Papa from "papaparse";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type { AuditTrailEntry, FormContextData } from "@thalamos/aspire-common";

import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { logActionToDatadog } from "~/tracing.js";
import { ukLocalFormatDateTime } from "~/util.js";

/** A single flat row of activity log data ready for export. */
export interface ActivityLogRow {
  timestamp: string;
  userName: string;
  teamName: string;
  message: string;
}

/** Context attached to every Datadog tracking event for activity log exports. */
export interface ActivityLogTrackingContext {
  type: "patient" | "formContext";
  formContextId?: string;
  patientId?: string;
}

/** Optional metadata rendered below the title on the first page of the PDF. */
export interface ExportMetadata {
  patientName?: string;
  nhsNumber?: string;
  formContextId?: string;
  formContextType?: string;
}

/**
 * Format an NHS number as 3-3-4 (e.g. "123 456 7890").
 * Returns `undefined` for null/undefined/blank values.
 */
export function formatNhsNumber(
  nhsNumber: string | null | undefined,
): string | undefined {
  if (!nhsNumber) return undefined;
  const digits = nhsNumber.replace(/\D/g, "");
  if (digits.length !== 10) return nhsNumber;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

/** Build the human-readable metadata lines shared across all export formats. */
function buildMetadataLines(metadata?: ExportMetadata): string[] {
  if (!metadata) return [];
  const parts: string[] = [];
  if (metadata.patientName) {
    parts.push(`Patient: ${metadata.patientName}`);
  }
  if (metadata.nhsNumber) {
    parts.push(`NHS Number: ${metadata.nhsNumber}`);
  }
  if (metadata.formContextType) {
    parts.push(`Type: ${metadata.formContextType}`);
  }
  if (metadata.formContextId) {
    parts.push(`Thalamos ID: ${metadata.formContextId}`);
  }
  return parts;
}

/**
 * Extract flat exportable rows from raw audit entries.
 * Mirrors the sort/filter/format logic in `Entries.tsx` so the exported
 * data matches exactly what appears on screen.
 */
export function extractActivityLogRows(
  rawAuditData: AuditTrailEntry[],
  getMessage: (
    audit: AuditTrailEntry,
    formContextData?: FormContextData,
  ) => string | string[] | null,
  formContextData?: FormContextData,
): ActivityLogRow[] {
  const sorted = [...rawAuditData].sort((a, b) =>
    dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? -1 : 1,
  );

  const rows: ActivityLogRow[] = [];

  for (const entry of sorted) {
    const message = getMessage(entry, formContextData);
    if (!message) continue;

    const messageText = (
      Array.isArray(message) ? message.join("\n") : message
    ).trim();

    const userName =
      entry.organisationName === "Thalamos"
        ? "Thalamos Admin"
        : (entry.userName ?? "");

    rows.push({
      timestamp: ukLocalFormatDateTime(entry.timestamp),
      userName,
      teamName: entry.teamName ?? "",
      message: messageText,
    });
  }

  return rows;
}

/** Copy the activity log to the clipboard as plain text. */
export async function copyActivityLogToClipboard(
  rows: ActivityLogRow[],
  tracking: ActivityLogTrackingContext,
  metadata?: ExportMetadata,
): Promise<void> {
  const metadataLines = buildMetadataLines(metadata);
  const entryLines = rows.map((r) => {
    const who = [r.userName, r.teamName].filter(Boolean).join(" — ");
    return `[${r.timestamp}] ${who ? `${who}: ` : ""}${r.message}`;
  });

  const sections: string[] = [];
  if (metadataLines.length > 0) {
    sections.push(metadataLines.join("\n"));
  }
  sections.push(entryLines.join("\n\n"));
  const text = sections.join("\n\n");

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    renderErrorToast({
      message:
        "Failed to copy to clipboard. Please try downloading as CSV or PDF instead.",
    });
    return;
  }

  logActionToDatadog("Activity log exported", {
    format: "clipboard",
    entryCount: rows.length,
    ...tracking,
  });

  renderSuccessToast({ message: "Activity log copied to clipboard" });
}

/** Download the activity log as a CSV file. */
export function downloadActivityLogCsv(
  rows: ActivityLogRow[],
  title: string,
  tracking: ActivityLogTrackingContext,
  metadata?: ExportMetadata,
): void {
  const patientName = metadata?.patientName ?? "";
  const nhsNumber = metadata?.nhsNumber ?? "";
  const formContextType = metadata?.formContextType ?? "";
  const formContextId = metadata?.formContextId ?? "";
  const hasMetadata = !!(
    patientName ||
    nhsNumber ||
    formContextType ||
    formContextId
  );

  const fields = hasMetadata
    ? [
        "Timestamp",
        "User",
        "Team",
        "Message",
        "Patient",
        "NHS Number",
        "Type",
        "Thalamos ID",
      ]
    : ["Timestamp", "User", "Team", "Message"];

  const data = rows.map((r) =>
    hasMetadata
      ? [
          r.timestamp,
          r.userName,
          r.teamName,
          r.message,
          patientName,
          nhsNumber,
          formContextType,
          formContextId,
        ]
      : [r.timestamp, r.userName, r.teamName, r.message],
  );

  const csv = Papa.unparse({ fields, data });

  const dataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  triggerDownload(dataUri, `${title}.csv`);

  logActionToDatadog("Activity log exported", {
    format: "csv",
    entryCount: rows.length,
    ...tracking,
  });
}

/** Download the activity log as a PDF file. */
export function downloadActivityLogPdf(
  rows: ActivityLogRow[],
  title: string,
  tracking: ActivityLogTrackingContext,
  metadata?: ExportMetadata,
): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  const titleLines = doc.splitTextToSize(title, usableWidth) as string[];
  doc.text(titleLines, margin, y);
  y += titleLines.length * 7 + 4;

  // Metadata subtitle block
  const subtitleParts = buildMetadataLines(metadata);
  if (subtitleParts.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    for (const part of subtitleParts) {
      doc.text(part, margin, y);
      y += 5;
    }
    y += 2;
  }

  // Entries
  doc.setFontSize(9);
  const lineHeight = 4;
  const entryGap = 5;
  const maxY = pageHeight - margin;

  /** Write a single line, adding a page break first if needed. */
  function writeLine(text: string, font: "bold" | "normal") {
    if (y + lineHeight > maxY) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", font);
    doc.text(text, margin, y);
    y += lineHeight;
  }

  for (const row of rows) {
    const header = [row.timestamp, row.userName, row.teamName]
      .filter(Boolean)
      .join("  |  ");

    doc.setFont("helvetica", "bold");
    const headerLines = doc.splitTextToSize(header, usableWidth) as string[];

    doc.setFont("helvetica", "normal");
    const messageLines = doc.splitTextToSize(
      row.message,
      usableWidth,
    ) as string[];

    // If the whole block fits on the current page, keep it together.
    // Otherwise start a new page so at least the header begins at the top.
    const blockHeight =
      (headerLines.length + messageLines.length) * lineHeight + entryGap;
    if (y + blockHeight > maxY && y > margin) {
      doc.addPage();
      y = margin;
    }

    for (const line of headerLines) {
      writeLine(line, "bold");
    }
    y += 1; // small gap between header and message

    for (const line of messageLines) {
      writeLine(line, "normal");
    }
    y += entryGap;
  }

  doc.save(`${title}.pdf`);

  logActionToDatadog("Activity log exported", {
    format: "pdf",
    entryCount: rows.length,
    ...tracking,
  });
}
